import { useCallback, useMemo, useContext, useState } from "react";
import { SnowplowTracker } from "~/utils/snowplow/snowplow_tracker.client";
import { useUrlToken } from "~/hooks/use_url_token";
import { useDevice } from "~/hooks/use_device";
import { TrackingContext } from "~/contexts/tracking_context";
import { PreviewContext } from "~/contexts/preview_context";

export enum IdentityType {
  ProfileId = "PROFILE_ID",
}

export enum SnowplowEvent {
  QuickApplyAutofillSuccess = "quick_apply_autofill_success",
  MyGreenhouseAdShown = "mygreenhouse.ad_shown",
  MyGreenhouseAdClicked = "mygreenhouse.ad_clicked",
}

const THROTTLE_DURATION = 100; // ms, dedupe events, esp. ad_shown mounting re-renders

export type TrackEventType = (
  eventName: SnowplowEvent,
  data: Record<PropertyKey, string | number | undefined>
) => undefined;

export type UpdateTrackingIdentityType = (
  identity: IdentityType,
  data: string | number
) => undefined;

export const useSnowplowTracker = () => {
  const isPreview = useContext(PreviewContext);
  const context = useContext(TrackingContext);
  const tracker = useMemo(() => SnowplowTracker && new SnowplowTracker(), []);
  const urlToken = useUrlToken();
  const { deviceType } = useDevice();
  const [_eventHistory, setEventHistory] = useState(
    (Object.keys(SnowplowEvent) as Array<keyof typeof SnowplowEvent>).reduce((acc, key) => {
      return { ...acc, [key]: 0 };
    }, {} as Record<SnowplowEvent, number>)
  );

  const userInfo = useMemo(() => {
    let user = {};
    if (context.jobSeekerProfileId) {
      user = { ...user, user_id: context.jobSeekerProfileId };
    }
    return user;
  }, [context]);

  const updateTrackingIdentity = useCallback(
    (identity, value) => {
      if (isPreview) {
        return;
      }

      switch (identity) {
        case IdentityType.ProfileId:
          context.setJobSeekerProfileId && context.setJobSeekerProfileId(value.toString());
          break;
        default:
          return;
      }
    },
    [context, isPreview]
  ) as UpdateTrackingIdentityType;

  const trackEventThrottledByEventType = useCallback(
    (eventName, data) => {
      if (isPreview) {
        return;
      }

      const now = Date.now();
      setEventHistory((history) => {
        if (now - history[eventName] < THROTTLE_DURATION) {
          return history;
        } else {
          tracker &&
            tracker.track({
              name: eventName,
              data: {
                ...userInfo,
                ...data,
                job_board_id: urlToken,
                device_type: deviceType,
                timestamp: now,
              },
            });
          return Object.assign(history, { [eventName]: now });
        }
      });
    },
    [isPreview, tracker, userInfo, setEventHistory, urlToken, deviceType]
  ) as TrackEventType;

  return { updateTrackingIdentity, trackEvent: trackEventThrottledByEventType };
};
